/**
 * Real install detection — replaces the self-reported "I've Installed It!"
 * funnel with verifiable browser events.
 *
 * Two signals:
 *   1. `appinstalled` window event — fires on Android Chrome / Desktop Chrome
 *      / Edge the moment the user accepts the install prompt. Source tag:
 *      'appinstalled'.
 *   2. `display-mode: standalone` on first load after install — covers iOS
 *      (which has no event) and any Android case where appinstalled was
 *      missed. Source tag: 'standalone_detected'. Guarded by a localStorage
 *      flag so we only report once per browser.
 *
 * Both signals POST to `/api/install-detected` with the user's bearer token.
 * Backend (the parallel Coder's `routes/installDetected.ts`) flips
 * `users.installed_at` + `install_source` on the first non-null update —
 * idempotent.
 *
 * The endpoint is brand-new and won't exist on staging until the backend PR
 * lands. We wrap every fetch in try/catch + console.warn so a 404 (or a
 * silent network failure) is invisible to the user.
 */

import { apiUrl } from './api-url';

const INSTALL_REPORTED_KEY = 'install_reported';
const STANDALONE_REPORT_DEDUP_MS = 5 * 60 * 1000; // not used yet but reserved

let initialized = false;

type InstallSource = 'appinstalled' | 'standalone_detected';

function getAuthToken(): string | null {
  try {
    return localStorage.getItem('papacito_token');
  } catch {
    return null;
  }
}

async function reportInstall(source: InstallSource): Promise<void> {
  const token = getAuthToken();
  // Backend requires auth to attribute the install to a user; if we don't
  // have one yet (logged-out page), just no-op. The next standalone visit
  // after login will catch it via the same code path.
  if (!token) return;
  try {
    await fetch(apiUrl('/install-detected'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ source }),
      // keepalive so the report survives a tab-close immediately after install.
      keepalive: true,
    });
  } catch (err) {
    // Backend endpoint may not exist yet on staging — keep this quiet.
    console.warn('[InstallDetection] /api/install-detected failed:', err);
  }
}

function isStandaloneNow(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  if (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }
  if ('standalone' in navigator && (navigator as { standalone?: boolean }).standalone === true) {
    return true;
  }
  return false;
}

/**
 * Wire `appinstalled` + first-standalone-visit detection. Idempotent — safe to
 * call multiple times (e.g. from main.tsx bootstrap AND from a route effect),
 * we just ignore subsequent calls.
 *
 * Returns a cleanup function for the `appinstalled` listener so test code can
 * unsubscribe. Production code typically discards it.
 */
export function setupInstallDetection(): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }
  if (initialized) {
    return () => {};
  }
  initialized = true;

  // 1. Event-based detection (Android Chrome, Desktop Chrome / Edge).
  const handleAppInstalled = () => {
    void reportInstall('appinstalled');
  };
  window.addEventListener('appinstalled', handleAppInstalled);

  // 2. Standalone-mode check on first load after install. iOS has no event,
  //    so we infer install from the first time the page loads in standalone
  //    display-mode. Guarded by localStorage so we only report once per
  //    browser install.
  try {
    if (isStandaloneNow()) {
      const already = localStorage.getItem(INSTALL_REPORTED_KEY);
      if (!already) {
        localStorage.setItem(INSTALL_REPORTED_KEY, '1');
        void reportInstall('standalone_detected');
      }
    }
  } catch {
    // localStorage can throw in private mode / disabled storage; we only lose
    // the dedupe, not the report itself.
    if (isStandaloneNow()) {
      void reportInstall('standalone_detected');
    }
  }

  return () => {
    window.removeEventListener('appinstalled', handleAppInstalled);
  };
}

// Exposed for direct callers (e.g. the "I've Installed It!" self-report
// button on /install-app). NOT subject to localStorage dedupe — backend
// handles idempotency. Source defaults to 'self_reported' which the backend
// flags differently from the verified sources.
export async function reportSelfInstall(): Promise<void> {
  const token = getAuthToken();
  if (!token) return;
  try {
    await fetch(apiUrl('/install-detected'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ source: 'self_reported' }),
      keepalive: true,
    });
  } catch (err) {
    console.warn('[InstallDetection] /api/install-detected (self) failed:', err);
  }
}

// Re-exported for callers that want to skip the helper and read the constant
// directly (e.g. tests verifying dedupe behavior).
export const __INSTALL_REPORTED_KEY = INSTALL_REPORTED_KEY;
export const __STANDALONE_REPORT_DEDUP_MS = STANDALONE_REPORT_DEDUP_MS;
